/**
 * "Inside Crown01 — From Our UK Warehouse" section.
 *
 * Demonstrates that Crown01 is a real UK supplier with genuine stock and
 * dispatch capability. Renders an asymmetric masonry-style gallery with a
 * keyboard-navigable lightbox (also swipeable on mobile via touch) and a
 * trust-stat strip underneath.
 *
 * Images live in /app/frontend/public/warehouse/*.jpg and are referenced by
 * filename. To swap any for a real photo: drop the same filename into that
 * folder. To reorder or change captions: edit the WAREHOUSE array below.
 */
import React, { useEffect, useRef, useState } from "react";

const WAREHOUSE = [
  { src: "/warehouse/crown01-team-uk-warehouse.jpg", alt: "The Crown01 warehouse team at our UK distribution centre with Union Jack",  caption: "Our UK team — Crown01 warehouse" },
  { src: "/warehouse/crown01-soft-serviettes-stock.jpg", alt: "Pallets of Crown01-branded Soft Serviettes stacked in our UK warehouse", caption: "Crown01 stock — palletised & ready" },
  { src: "/warehouse/crown01-warehouse-aisle.jpg",   alt: "Inside the Crown01 UK warehouse aisle",                                    caption: "Warehouse aisle — full inventory" },
  { src: "/warehouse/crown01-warehouse-real.jpg",    alt: "Inside the Crown01 UK warehouse",                                          caption: "Inside our Enfield warehouse" },
  { src: "/warehouse/refuse-sacks-stock.jpg",        alt: "Heavy-duty refuse sacks bulk stock",                                       caption: "Refuse sacks — every size, in bulk" },
  { src: "/warehouse/bulk-order-prep.jpg",           alt: "Bulk pallet order being prepared",                                         caption: "Bulk and pallet orders — built to order" },
];

// Masonry "row spans" — gives the gallery an editorial, premium asymmetric look.
// Pattern over 6 cards (the 1st and 4th are taller for visual rhythm).
const SPANS = ["row-span-2", "", "", "row-span-2", "", ""];

export default function InsideCrown01() {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const open = lightboxIdx !== null;
  const close = () => setLightboxIdx(null);
  const next = () => setLightboxIdx((i) => (i + 1) % WAREHOUSE.length);
  const prev = () => setLightboxIdx((i) => (i - 1 + WAREHOUSE.length) % WAREHOUSE.length);

  // Keyboard nav + body-scroll lock while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  // Touch swipe handling on the lightbox image
  const touch = useRef({ x: 0, t: 0 });
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, t: Date.now() }; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dt = Date.now() - touch.current.t;
    if (dt < 600 && Math.abs(dx) > 40) (dx < 0 ? next : prev)();
  };

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="inside-crown01">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="grid md:grid-cols-[1fr_1fr] gap-6 md:gap-12 items-end">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Inside Crown01</div>
            <h2 className="font-display text-3xl md:text-5xl text-black mt-3 leading-[1.05]">
              Real Stock. Real Warehouse.<br />Real UK Supplier.
            </h2>
            <div className="mt-4 h-[3px] w-16" style={{ background: "var(--c-gold)" }}></div>
          </div>
          <p className="text-sm md:text-base text-[color:var(--c-grey-700)] leading-relaxed md:text-right">
            Every order is fulfilled from our UK warehouse, with stock ready for <strong className="text-black">retail, trade, wholesale and pallet</strong> customers. No drop-shipping, no middlemen — just genuine inventory and same-day dispatch from <strong className="text-black">Enfield, EN2 9BS</strong>.
          </p>
        </div>

        {/* Masonry gallery */}
        <div
          className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-[160px] md:auto-rows-[200px]"
          data-testid="warehouse-gallery"
        >
          {WAREHOUSE.map((img, i) => (
            <button
              key={img.src}
              onClick={() => setLightboxIdx(i)}
              className={`group relative overflow-hidden rounded-2xl ${SPANS[i] || ""} cursor-zoom-in`}
              style={{ border: "1px solid var(--c-grey-100)" }}
              data-testid={`warehouse-card-${i}`}
              aria-label={`Open larger image — ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              {/* Gradient + caption */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)" }} />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 flex items-end justify-between gap-2 text-white">
                <span className="text-[10px] md:text-xs uppercase tracking-[0.16em] font-semibold drop-shadow-sm">
                  {img.caption}
                </span>
                <span className="opacity-90 group-hover:opacity-100 transition">
                  <i className="fa-solid fa-magnifying-glass-plus text-xs"></i>
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Trust stats */}
        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="warehouse-stats">
          {[
            { i: "fa-warehouse",     t: "UK Warehouse",                s: "Enfield, EN2 9BS" },
            { i: "fa-boxes-stacked", t: "Thousands in stock",          s: "Genuine inventory, ready to ship" },
            { i: "fa-shop",          t: "Retail & Wholesale Supply",   s: "Single packs to full pallets" },
          ].map((x) => (
            <div
              key={x.t}
              className="rounded-2xl p-4 md:p-5 flex items-start gap-3"
              style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}
              data-testid={`warehouse-stat-${x.t.split(" ")[0].toLowerCase()}`}
            >
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--c-black)" }}>
                <i className={`fa-solid ${x.i}`} style={{ color: "var(--c-gold)" }} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] md:text-xs uppercase tracking-[0.14em] font-semibold text-[color:var(--c-gold-deep)]">{x.t}</div>
                <div className="text-sm md:text-base font-display text-black mt-0.5 leading-snug">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/92 flex flex-col items-center justify-center p-4 md:p-8"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="dialog"
          aria-label="Warehouse image viewer"
          data-testid="warehouse-lightbox"
        >
          <button
            onClick={close}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Close"
            data-testid="warehouse-lightbox-close"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
          <img
            src={WAREHOUSE[lightboxIdx].src}
            alt={WAREHOUSE[lightboxIdx].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[78vh] md:max-h-[85vh] object-contain rounded-lg"
          />
          <div className="mt-3 text-white/85 text-sm text-center" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-base md:text-lg">{WAREHOUSE[lightboxIdx].caption}</div>
            <div className="text-white/55 text-xs mt-1">{lightboxIdx + 1} / {WAREHOUSE.length}</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Previous"
            data-testid="warehouse-lightbox-prev"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Next"
            data-testid="warehouse-lightbox-next"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </section>
  );
}
